import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { useState, type MouseEvent } from 'react';
import type { ExportGroupFailure } from '../types';

interface ExportFailureIndicatorProps {
  readonly systemName: string;
  readonly failures: readonly ExportGroupFailure[];
  readonly onOpenExportLog?: () => void;
}

export default function ExportFailureIndicator({
  systemName,
  failures,
  onOpenExportLog,
}: ExportFailureIndicatorProps) {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  if (failures.length === 0) {
    return null;
  }

  const closePopover = () => setAnchorElement(null);
  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };
  const handleOpenLog = () => {
    closePopover();
    onOpenExportLog?.();
  };

  return (
    <>
      <IconButton
        color="error"
        size="small"
        aria-label={`Review latest PCF export failure for ${systemName}`}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorElement)}
        onClick={handleOpen}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{ p: 0.25, flexShrink: 0 }}
      >
        <ErrorOutlineIcon sx={{ fontSize: '1.05rem' }} />
      </IconButton>
      <Popover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box
          role="dialog"
          aria-label={`PCF not exported for ${systemName}`}
          sx={{ width: { xs: 280, sm: 340 }, maxWidth: 'calc(100vw - 32px)', p: 1.5 }}
        >
          <Typography variant="subtitle2" sx={{ color: 'error.main', mb: 1 }}>
            PCF not exported for {systemName}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            {failures.map((failure, index) => (
              <Box component="li" key={`${failure.systemName}-${index}`} sx={{ mb: 1 }}>
                <Typography variant="body2">{failure.error}</Typography>
                {failure.suggestedAction ? (
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>What to do: </Box>
                    {failure.suggestedAction}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
          {onOpenExportLog ? (
            <Button size="small" onClick={handleOpenLog} sx={{ mt: 0.25 }}>
              Open export log
            </Button>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}
